/**
 * Web Serial driver for the Ingenico Desk5000 payment terminal.
 *
 * Implements the Ingenico serial protocol (Protocole Concert V3):
 *   ENQ/ACK handshake, STX..ETX+LRC framing, dialogue loop.
 *
 * This is a faithful port of the original Kivy/Python driver to
 * the Web Serial API, keeping identical timing and byte-level behavior.
 */

import { TPE } from '../lib/constants';
import { calculateLrc } from '../lib/lrc';
import { useHardwareStore } from '../store/hardware-store';
import type { PaymentResult } from './types';

export class TpeSerial {
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private connected = false;

  // ---------- Connection lifecycle ----------

  /**
   * Try to automatically reconnect to a previously granted serial port.
   *
   * Web Serial API remembers ports the user has previously granted access to.
   * For each remembered port, we attempt to open it and verify the TPE
   * responds to an ENQ/ACK handshake.
   *
   * @returns true if auto-connect succeeded, false if manual selection is needed.
   */
  async autoConnect(): Promise<boolean> {
    try {
      if (typeof navigator?.serial?.getPorts !== 'function') {
        return false;
      }

      const ports = await navigator.serial.getPorts();
      if (ports.length === 0) return false;

      for (const port of ports) {
        try {
          await port.open({
            baudRate: TPE.BAUD_RATE,
            dataBits: 8,
            parity: 'none',
            stopBits: 1,
          });

          if (!port.readable || !port.writable) {
            await port.close().catch(() => {});
            continue;
          }

          this.port = port;
          this.reader = port.readable.getReader();
          this.writer = port.writable.getWriter();

          // Verify TPE responds to handshake
          const alive = await this.handshake();
          if (alive) {
            this.connected = true;
            console.info('[TPE] Auto-connected to remembered port');
            return true;
          }

          // Handshake failed — cleanup and try next port
          await this.disconnect();
        } catch {
          // Port already open or inaccessible — try next
          await port.close().catch(() => {});
        }
      }

      return false;
    } catch (err) {
      console.warn('[TPE] autoConnect failed:', err);
      return false;
    }
  }

  /**
   * Connect to the TPE. First tries auto-connect to a previously
   * granted port. If that fails, prompts the user to select a port manually.
   */
  async connect(): Promise<boolean> {
    // Try remembered ports first (no user interaction needed)
    const autoOk = await this.autoConnect();
    if (autoOk) return true;

    // Fall back to manual port selection
    try {
      this.port = await navigator.serial.requestPort();
      await this.port.open({
        baudRate: TPE.BAUD_RATE,
        dataBits: 8,
        parity: 'none',
        stopBits: 1,
      });

      if (this.port.readable) {
        this.reader = this.port.readable.getReader();
      }
      if (this.port.writable) {
        this.writer = this.port.writable.getWriter();
      }

      if (!this.reader || !this.writer) {
        await this.disconnect();
        return false;
      }

      this.connected = true;
      return true;
    } catch (err) {
      console.error('[TPE] connect failed:', err);
      this.connected = false;
      return false;
    }
  }

  /** Release the reader/writer and close the serial port. */
  async disconnect(): Promise<void> {
    try {
      if (this.reader) {
        await this.reader.cancel().catch(() => {});
        this.reader.releaseLock();
        this.reader = null;
      }
      if (this.writer) {
        await this.writer.close().catch(() => {});
        this.writer.releaseLock();
        this.writer = null;
      }
      if (this.port) {
        await this.port.close().catch(() => {});
        this.port = null;
      }
    } finally {
      this.connected = false;
    }
  }

  // ---------- Handshake ----------

  /**
   * Send ENQ (0x05) and expect ACK (0x06) within 2 seconds.
   * This is the standard Ingenico "are you there?" probe.
   */
  async handshake(): Promise<boolean> {
    if (!this.writer || !this.reader) return false;

    await this.writer.write(new Uint8Array([TPE.ENQ]));

    const byte = await this.readByteWithTimeout(TPE.HANDSHAKE_TIMEOUT);
    return byte === TPE.ACK;
  }

  // ---------- Payment flow ----------

  /**
   * Execute a full card payment dialogue.
   *
   * @param amountCents - Amount in euro-cents (e.g. 1250 = 12.50 EUR).
   * @returns A PaymentResult describing the terminal's answer.
   *
   * Protocol steps:
   *   1. Build a payment frame: STX + "00" + 8-digit amount + "1" + ETX + LRC
   *   2. Perform ENQ/ACK handshake to wake the terminal
   *   3. Wait PRE_SEND_DELAY ms, then transmit the frame
   *   4. Enter the dialogue loop (see `dialogueLoop`)
   */
  async pay(amountCents: number): Promise<PaymentResult> {
    const start = Date.now();

    if (!this.writer || !this.reader) {
      return this.makeResult('ERROR', false, 0, start, 'TPE not connected');
    }

    // ---- 1. Build the payment frame ----
    const amountStr = amountCents.toString().padStart(8, '0');
    const bodyStr = `00${amountStr}1`;
    const bodyBytes = new TextEncoder().encode(bodyStr);

    // Frame = STX + body + ETX
    const frame = new Uint8Array(bodyBytes.length + 2);
    frame[0] = TPE.STX;
    frame.set(bodyBytes, 1);
    frame[frame.length - 1] = TPE.ETX;

    // LRC is computed over body + ETX (everything after STX)
    const lrcData = frame.slice(1); // body + ETX
    const lrc = calculateLrc(lrcData);

    // Full message = frame + LRC byte
    const message = new Uint8Array(frame.length + 1);
    message.set(frame);
    message[message.length - 1] = lrc;

    // ---- 2. Handshake ----
    const handshakeOk = await this.handshake();
    if (!handshakeOk) {
      return this.makeResult('ERROR', false, 0, start, 'Handshake failed');
    }

    // ---- 3. Send the payment frame ----
    await this.delay(TPE.PRE_SEND_DELAY);
    await this.writer.write(message);

    // ---- 4. Dialogue loop ----
    return this.dialogueLoop(start);
  }

  // ---------- Heartbeat ----------

  /** Quick ENQ/ACK ping to verify the terminal is still responsive. */
  async checkAlive(): Promise<boolean> {
    try {
      return await this.handshake();
    } catch {
      return false;
    }
  }

  // ---------- State ----------

  get isConnected(): boolean {
    return this.connected;
  }

  // ========== Private helpers ==========

  /**
   * Main dialogue loop after sending a payment frame.
   *
   * The terminal goes through several exchanges:
   *   - First ACK: confirms it received the payment frame.
   *   - One or more ENQ from the terminal: we must reply ACK immediately.
   *   - A result frame (STX .. ETX + LRC): contains the outcome.
   *
   * Two timeout levels:
   *   - DIALOGUE_TIMEOUT (70s): triggers PAPER_ERROR (printer jam on terminal side)
   *   - FATAL_TIMEOUT (200s): absolute abort
   */
  private async dialogueLoop(start: number): Promise<PaymentResult> {
    let ackPaymentReceived = false;
    let dialogueCount = 0;
    let frameBuffer: number[] = [];
    let inFrame = false;

    const abortController = new AbortController();
    const fatalTimer = setTimeout(() => abortController.abort(), TPE.FATAL_TIMEOUT);

    try {
      while (!abortController.signal.aborted) {
        const elapsed = Date.now() - start;

        // Dialogue-level timeout (paper error heuristic)
        if (ackPaymentReceived && elapsed > TPE.DIALOGUE_TIMEOUT) {
          useHardwareStore.getState().setTpePaperStatus('EMPTY');
          return this.makeResult('PAPER_ERROR', false, dialogueCount, start);
        }

        // Read next chunk from the serial port
        const byte = await this.readByteWithTimeout(
          TPE.DIALOGUE_TIMEOUT - (Date.now() - start),
        );

        if (byte === null) {
          // Timeout waiting for data
          if (ackPaymentReceived) {
            useHardwareStore.getState().setTpePaperStatus('EMPTY');
            return this.makeResult('PAPER_ERROR', false, dialogueCount, start);
          }
          return this.makeResult('TIMEOUT', false, dialogueCount, start);
        }

        // --- ACK from terminal (first one = payment frame received) ---
        if (byte === TPE.ACK && !ackPaymentReceived) {
          ackPaymentReceived = true;
          continue;
        }

        // --- ENQ from terminal: dialogue request, reply ACK immediately ---
        if (byte === TPE.ENQ && ackPaymentReceived) {
          dialogueCount++;
          await this.writer!.write(new Uint8Array([TPE.ACK]));
          continue;
        }

        // --- STX: start of a result frame ---
        if (byte === TPE.STX) {
          inFrame = true;
          frameBuffer = [];
          continue;
        }

        // --- ETX: end of the result frame ---
        if (byte === TPE.ETX && inFrame) {
          // Next byte is LRC (consume it but we trust the terminal)
          await this.readByteWithTimeout(500);

          // Send EOT to signal we are done
          await this.writer!.write(new Uint8Array([TPE.EOT]));

          // Parse the response
          return this.parseResultFrame(frameBuffer, dialogueCount, start);
        }

        // --- Accumulate body bytes inside a frame ---
        if (inFrame) {
          frameBuffer.push(byte);
        }
      }

      // Abort controller fired
      return this.makeResult('TIMEOUT', false, dialogueCount, start, 'Fatal timeout');
    } finally {
      clearTimeout(fatalTimer);
    }
  }

  /**
   * Parse the body of a result frame returned by the terminal.
   *
   * The first 3 bytes are the response code:
   *   - "000" = ACCEPTED (payment approved)
   *   - "001"-"099" = REFUSED (various decline codes)
   *   - Anything else = ERROR
   */
  private parseResultFrame(
    body: number[],
    dialogueCount: number,
    start: number,
  ): PaymentResult {
    const raw = String.fromCharCode(...body);

    if (body.length < 3) {
      return this.makeResult('ERROR', false, dialogueCount, start, `Short frame: ${raw}`);
    }

    const code = raw.substring(0, 3);

    if (code === '000') {
      useHardwareStore.getState().setTpePaperStatus('OK');
      return this.makeResult('ACCEPTED', true, dialogueCount, start, undefined, raw);
    }

    if (code === '001') {
      return this.makeResult('REFUSED', false, dialogueCount, start, undefined, raw);
    }

    // Other numeric codes are also refusals/errors
    const codeNum = parseInt(code, 10);
    if (!isNaN(codeNum) && codeNum > 1 && codeNum < 100) {
      return this.makeResult('REFUSED', false, dialogueCount, start, `Code ${code}`, raw);
    }

    return this.makeResult('ERROR', false, dialogueCount, start, `Unknown code: ${code}`, raw);
  }

  /**
   * Read a single byte from the serial reader, with a timeout.
   * Returns null if the timeout elapses before data arrives.
   */
  private async readByteWithTimeout(timeoutMs: number): Promise<number | null> {
    if (!this.reader || timeoutMs <= 0) return null;

    return new Promise<number | null>((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve(null);
        }
      }, timeoutMs);

      this.reader!
        .read()
        .then(({ value, done }) => {
          clearTimeout(timer);
          if (settled) return;
          settled = true;

          if (done || !value || value.length === 0) {
            resolve(null);
          } else {
            // Return the first byte; remaining bytes in the chunk will
            // be picked up by the next read() call from the stream.
            resolve(value[0]);
          }
        })
        .catch(() => {
          clearTimeout(timer);
          if (!settled) {
            settled = true;
            resolve(null);
          }
        });
    });
  }

  /** Build a PaymentResult object. */
  private makeResult(
    responseType: PaymentResult['responseType'],
    success: boolean,
    dialogueCount: number,
    start: number,
    error?: string,
    rawData?: string,
  ): PaymentResult {
    return {
      success,
      responseType,
      dialogueCount,
      elapsedMs: Date.now() - start,
      ...(rawData !== undefined && { rawData }),
      ...(error !== undefined && { error }),
    };
  }

  /** Promise-based delay. */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
